<template>
  <div class="flex-1">
    <h1 class="font-semibold leading-none">
      Health System - Last 7 Days
    </h1>
    <div class="pt-2">
      <Chart
        v-if="params.startDate"
        type="bar"
        :data="chartData"
        :options="chartOptions"
        style="height: 100px;"
      />
      <div
        v-else
        class="flex flex-1 w-full items-center justify-center"
      >
        <ProgressSpinner />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useDark } from '@vueuse/core'
import { useRouter } from 'vue-router'

const injectCSS = css => {
  const el = document.createElement('style')
  el.innerText = css
  document.head.appendChild(el)
  return el
}

const params = defineModel({ type: Object, default: { startDate: undefined, endDate: undefined, anomalies: [] } })
const darkMode = useDark()
const router = useRouter()
let values = []

const chartData = computed(() => {
  const documentStyle = getComputedStyle(document.documentElement)
  if (!params.value.anomalies.length) {
    return {
      labels: [''],
      datasets: [{
        type: 'bar',
        backgroundColor: '#34D399',
        maxBarThickness: 20,
        data: [new Date().getTime()],
      }],
    }
  }
  const len = params.value.anomalies.length
  const datasets = [{
    type: 'bar',
    backgroundColor: '#34D399',
    maxBarThickness: 20,
    data: [new Date(params.value.anomalies[params.value.anomalies.length - 1].startdate).getTime()],
  }]
  values = [new Date(params.value.anomalies[params.value.anomalies.length - 1].startdate).getTime()]

  params.value.anomalies.slice().reverse().forEach((x, i) => {
    datasets.push({
      type: 'bar',
      backgroundColor: documentStyle.getPropertyValue('--p-red-500'),
      maxBarThickness: 25,
      data: [new Date(x.enddate || Date.now()).getTime() - new Date(x.startdate).getTime()],
    })
    values.push(new Date(x.enddate || '').getTime() - new Date(x.startdate).getTime() + values[values.length - 1])
    if (x.enddate) {
      datasets.push({
        type: 'bar',
        backgroundColor: '#34D399',
        maxBarThickness: 20,
        data: [new Date(params.value.anomalies[len - i - 2]?.startdate || Date.now()).getTime() - new Date(x.enddate).getTime()],
      })
      values.push(new Date(params.value.anomalies[len - i - 2]?.startdate || Date.now()).getTime() - new Date(x.enddate).getTime() + values[values.length - 1])
    }
  })

  return {
    labels: [''],
    datasets,
  }
})

const chartOptions = computed(() => {
  const ticks = []
  for (let i = params.value.startDate.getTime(); i < params.value.endDate.getTime(); i += (1000 * 60 * 60 * 24)) {
    ticks.push(i)
  }

  return {
    indexAxis: 'y',
    barPercentage: 1,
    categoryPercentage: 1,
    maintainAspectRatio: false,
    onHover: (event, clickedElements) => {
      if (!clickedElements.length || !(clickedElements[0].datasetIndex % 2)) {
        injectCSS('body { cursor: default }')
      } else {
        injectCSS('body { cursor: pointer }')
      }
    },
    onClick: (event, clickedElements) => {
      if (!clickedElements.length || !(clickedElements[0].datasetIndex % 2)) return
      const i = (clickedElements[0].datasetIndex - 1) / 2 + 1
      const len = params.value.anomalies.length
      router.push(`/anomalies/${params.value.anomalies[len - i].id}`)
    },

    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        xAlign: 'left',
        yAlign: 'center',
        callbacks: {
          title: (item) => {
            if (item[0].datasetIndex % 2) {
              const i = (item[0].datasetIndex - 1) / 2 + 1
              const len = params.value.anomalies.length
              return `Start: ${new Date(params.value.anomalies[len - i].startdate).toLocaleString()}\nEnd: ${new Date(params.value.anomalies[len - i].enddate || Date.now()).toLocaleString()}`
            }
            return 'No issues'
          },
          label: () => '',
          footer: (item) => {
            if (item[0].datasetIndex % 2) {
              return 'Click for details'
            }
            return ''
          },
        }
      }
    },
    scales: {
      x: {
        border: {
          color: darkMode.value ? 'rgba(255, 255, 255, 0.2)' : undefined,
          width: 1
        },
        stacked: true,
        min: params.value.startDate.getTime(),
        max: params.value.endDate.getTime(),
        afterBuildTicks: axis => (axis.ticks = ticks.map(v => ({ value: v }))),
        ticks: {
          color: darkMode.value ? '#888888' : undefined,
          beginAtZero: true,
          callback: (value, index, ticks) => new Date(value).toLocaleDateString('en-IT', { weekday: 'short', month: 'short', day: 'numeric' })
        },
        grid: {
          drawTicks: true,
          color: darkMode.value ? 'rgba(255, 255, 255, 0.2)' : undefined
        },
      },
      y: {
        border: {
          color: darkMode.value ? 'rgba(255, 255, 255, 0.2)' : undefined,
          width: 0
        },
        stacked: true,
        barThickness: 0,
        maxBarThickness: 0,
        tickColor: 'red',
        ticks: {
          color: darkMode.value ? '#888888' : undefined,
        },
        grid: {
          drawTicks: true,
          color: darkMode.value ? 'rgba(255, 255, 255, 0.2)' : undefined
        },
      },
    },
  }
})
</script>
